// Added by Sejune Oh on 2019-05-27
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import RootStore from '../..'
import MgmtStoreBase from '../../base/MgmtStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface DcmSCPRulesetBaseElementProps {
  id?: string
  name?: string
  type?: string
  description?: string
  rule_data?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export interface DcmSCPRulesetElementProps extends DcmSCPRulesetBaseElementProps {}

export interface DcmSCPRuleElementProps {
  name?: string
  relation?: 'AND' | 'OR'
  conditions?: Array<DcmSCPConditionElementProps>
  tds_ids?: Array<string>
}

export interface DcmSCPConditionElementProps {
  func_macro?: string
  tag_macro?: string
  values?: Array<string>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DicomScpRulesetStore extends MgmtStoreBase<DcmSCPRulesetElementProps> {
  totalCount: number = 0
  elements: Array<DcmSCPRulesetElementProps> = []
  cachedDcmSCPRulset?: DcmSCPRulesetElementProps

  root?: RootStore

  constructor(root: RootStore) {
    super({ storeName: 'RulesetStore', apiUrl: 'mgmt/dicomscp-rulesets', _root: root })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      cachedDcmSCPRulset: observable,
      _setTotalCount: action,
      setElements: action,
      cacheDcmSCPRuleset: action,
      clearCachedDcmSCPRuleset: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElement = async (id: string) => {
    return await super.getSingle(id)
  }

  getElements = async (path: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<DcmSCPRulesetElementProps>
  }

  addElement = async (element: DcmSCPRulesetElementProps) => {
    const newElement = await super.add(element)
    return newElement.id
  }

  removeElement = async (id: string) => {
    return await super.remove(id)
  }

  updateElement = async (id: string, element: DcmSCPRulesetElementProps) => {
    const updatedElement = await super.updateSingle(id, element)
    return updatedElement.id
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  checkDuplicatedRulesetName = (nodeName: string) => {
    return super.checkDuplicatedByField({ field: 'name', value: nodeName })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<DcmSCPRulesetElementProps>) => {
    this.elements = elements
  }

  cacheDcmSCPRuleset = (ruleset: DcmSCPRulesetElementProps) => {
    this.cachedDcmSCPRulset = ruleset
  }

  clearCachedDcmSCPRuleset = () => {
    this.cachedDcmSCPRulset = undefined
  }
}
